import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { message } from 'antd'
import WrappedModal from '@/components/WrappedModal'
import NotificationRuleForm, {
  channelsIncludeSms,
  normalizeTemplateVarSlotsIfConfigured,
  parseTemplateVarAllowedKeysJson
} from './NotificationRuleForm'
import {
  createNotificationRuleAction,
  updateNotificationRuleAction
} from '@/redux/order/serviceNotificationSlice'

/**
 * 通知规则弹窗：`WrappedModal` + `NotificationRuleForm`。
 *
 * **Modal 外层 props**：`baseProps`、`children`、`title`、`width`；`dispatch` 由 Wrapper 注入。
 *
 * **`baseProps`（与表单契约）**：业务字段整块交给 Form（见 `NotificationRuleForm` 顶部注释及
 * `.cursor/rules/modal-submit-refresh-flow.mdc` 第六节「通知规则」表）。Modal 另只读其中：
 * `actType`、`editingRule`（编辑取 id / formKey）、`onSuccess`、`onCancel`。
 *
 * **提交边界**（单一路径）：`validateFields` → `buildNotificationRulePayload` → 创建/更新 thunk。
 * 表单已去掉「触发方式」：编辑时 `triggerType` 由 `handleOk` 从 `editingRule` 写入请求体，避免误清空。
 */
function isPlainObject (value) {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function pickErrorMessage (err) {
  if (typeof err === 'string') return err
  if (isPlainObject(err)) {
    if (typeof err.message === 'string') return err.message
    if (typeof err.msg === 'string') return err.msg
    if (typeof err.error === 'string') return err.error
  }
  return '操作失败'
}

// 仅 actType === 'edit' 为编辑；缺省及其它值一律按新建。
function modalActType (baseProps = {}) {
  return (baseProps || {}).actType === 'edit' ? 'edit' : 'create'
}

/**
 * 表单 / 接口混用场景：将 targets 规范为可 JSON 序列化的请求体数组。
 * - 支持已是数组，或 **JSON 字符串**（如从接口原样带回的字符串字段）
 * - 兼容 target_id；targetId 须为有限数字
 * - phone 非空才写入
 */
export function parseNotificationRuleTargets (raw) {
  let list = []
  if (raw == null || raw === '') return list
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (s === '') return list
    try {
      const parsed = JSON.parse(s)
      list = Array.isArray(parsed) ? parsed : []
    } catch {
      return list
    }
  } else if (Array.isArray(raw)) {
    list = raw
  } else {
    return list
  }

  const out = []
  for (const t of list) {
    if (!isPlainObject(t)) continue
    const idRaw =
      t.targetId !== undefined && t.targetId !== null ? t.targetId : t.target_id
    if (idRaw === undefined || idRaw === null || idRaw === '') continue
    const tid = Number(idRaw)
    if (!Number.isFinite(tid)) continue
    const row = { targetId: tid }
    const phone =
      t.phone !== undefined && t.phone !== null ? String(t.phone).trim() : ''
    if (phone !== '') row.phone = phone
    out.push(row)
  }
  return out
}

/**
 * 组装 POST/PUT /notification/rule 请求体。
 * 表单字段与接口基本一致：先铺开 values，排除仅表单存在的 templateVarSlots / scene，再覆盖需转换的字段。
 * `scene`（通知场景）提交为接口字段 `notifyCategory`。
 * storeId 为必选下拉，`validateFields()` 通过后直接转数字即可。
 * @param {object} values validateFields 结果
 */
export function buildNotificationRulePayload (values) {
  const {
    scene,
    templateVarSlots,
    targets,
    storeId,
    name,
    templateId,
    channels,
    offsetValue,
    enabled,
    _templateVarAllowedKeysJson,
    _templateVarKeysOptsLoaded,
    ...rest
  } = values

  const body = {
    ...rest,
    notifyCategory: String(scene ?? '').trim(),
    storeId: Number(storeId),
    name: String(name ?? '').trim(),
    offsetValue:
      offsetValue !== undefined && offsetValue !== null ? Number(offsetValue) : 0,
    channels: Array.isArray(channels) ? channels.map((c) => String(c)) : [],
    enabled: enabled !== undefined ? enabled : true
  }

  /** 含短信时才上送 templateId（选短信时表单已必填）；variable 顺序仅在此场景且用户已配置 slots 时上送 */
  if (channelsIncludeSms(body.channels)) {
    body.templateId = String(templateId ?? '').trim()
  }

  const targetsPayload = parseNotificationRuleTargets(targets)
  if (targetsPayload.length) body.targets = targetsPayload

  const allowedTplKeys = parseTemplateVarAllowedKeysJson(
    _templateVarAllowedKeysJson,
    _templateVarKeysOptsLoaded === '1'
  )
  const slots = normalizeTemplateVarSlotsIfConfigured(
    templateVarSlots,
    allowedTplKeys
  )
  if (body.templateId && Array.isArray(slots) && slots.length) {
    body.templateVarKeys = slots.map((slot) => String(slot.key))
  }

  return body
}

class NotificationRuleModal extends React.Component {
  static propTypes = {
    /**
     * 业务入参与回调，与传给 WrappedModal 的 `baseProps` 同源并会摊平 merge 至 Form；
     * 表单业务字段以 `NotificationRuleForm` 的 **`props.baseProps` 嵌套对象** 为准（字段表见规则文档）。
     */
    baseProps: PropTypes.object,
    children: PropTypes.node,
    title: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dispatch: PropTypes.func.isRequired
  }

  handleOk = async (formRef) => {
    const bp = this.props.baseProps || {}
    const { onSuccess } = bp
    const { dispatch } = this.props

    try {
      const actType = modalActType(bp)

      const values = await formRef.current.validateFields()
      const body = buildNotificationRulePayload(values)

      if (actType === 'edit' && isPlainObject(bp.editingRule)) {
        const t = bp.editingRule.triggerType
        const keep =
          t !== undefined &&
          t !== null &&
          !(typeof t === 'string' && t.trim() === '')
        if (keep) {
          body.triggerType = String(t)
        }
      }

      if (actType === 'edit') {
        const record = bp.editingRule
        const id = isPlainObject(record) ? record.id : undefined
        if (id == null || id === '') {
          throw new Error('编辑提交缺少规则 id')
        }
        console.log('body', body)
        await dispatch(updateNotificationRuleAction({ id, body })).unwrap()
        message.success('规则已更新')
      } else {
        await dispatch(createNotificationRuleAction(body)).unwrap()
        message.success('规则创建成功')
      }

      if (typeof onSuccess === 'function') {
        onSuccess()
      }
    } catch (err) {
      message.error(pickErrorMessage(err))
      throw err
    }
  }

  handleCancel = () => {
    const { baseProps } = this.props
    const { onCancel } = baseProps || {}
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  render () {
    const { children, baseProps, title, width } = this.props
    const bp = baseProps || {}
    const actType = modalActType(bp)
    const formKey =
      actType === 'edit' && bp.editingRule != null && bp.editingRule.id != null
        ? `edit-${bp.editingRule.id}`
        : `create-${bp.defaultScene}`

    const baseComponent = (
      <NotificationRuleForm formKey={formKey} actType={actType} baseProps={bp} />
    )

    const modalTitle =
      title !== undefined && title !== null && title !== ''
        ? title
        : actType === 'edit'
          ? '编辑规则'
          : '新增规则'

    return (
      <WrappedModal
        children={children}
        baseProps={{ ...baseProps }}
        baseComponent={baseComponent}
        title={modalTitle}
        width={width || 640}
        onOk={this.handleOk}
        onCancel={this.handleCancel}
        centerTitle
        className="center-title-modal"
        maskClosable={false}
      />
    )
  }
}

const NotificationRuleModalWrapper = (props) => {
  const dispatch = useDispatch()
  return <NotificationRuleModal {...props} dispatch={dispatch} />
}

export default NotificationRuleModalWrapper
